import { useId, type CSSProperties, type ReactNode } from 'react';

// Nova — the NovaPSA red-panda mascot. ONE owned, license-clean SVG (an original stylised
// drawing informed by CC0 reference photos, not a stock trace). Two silhouettes:
//   'mark' — front-facing head, the compact brand logo (rust face, white splotch markings,
//            tear-lines, black nose + thin mouth, proportionate white-lined ears; reads to ~16px).
//   'full' — a sitting Nova: rust shoulders/head gradient-blending down into a dark chest,
//            belly and legs, with a ringed bushy tail.
// Softness comes from GRADIENT shading (not spiky fur edges). `mood` swaps her expression for
// the assistant + celebrations. Animated via nova.css (blink/bob/tail-sway), stilled under
// prefers-reduced-motion. Decorative by default (aria-hidden); pass `title` for an a11y name.
//
// NOTE ON COLOUR: the gradient stops below are Nova's fixed ILLUSTRATION palette — a real red
// panda's colours, deliberately the same in light and dark. They are not the themeable UI
// tokens (those stay in tokens.css); only the soft ground halo reads --nova-ground.

export type NovaMood = 'happy' | 'wave' | 'think' | 'sleep' | 'cheer';
export type NovaVariant = 'mark' | 'full';

export interface NovaProps {
  variant?: NovaVariant;
  mood?: NovaMood;
  /** px (number) or any CSS length. Defaults: mark 24, full 96. */
  size?: number | string;
  /** Idle motion (blink/bob/tail). Ignored under prefers-reduced-motion (nova.css). */
  animated?: boolean;
  /** Soft halo behind her (full variant only). */
  ground?: boolean;
  /** Accessible name. Omit → decorative (aria-hidden). */
  title?: string;
  className?: string;
  style?: CSSProperties;
}

// Gradient defs, id-prefixed so many Nova instances on one page never collide.
function Defs({ id }: { id: string }): ReactNode {
  return (
    <defs>
      <radialGradient id={`${id}-head`} cx="50%" cy="38%" r="70%">
        <stop offset="0" stopColor="#d16c3d" />
        <stop offset="0.6" stopColor="#c05a2f" />
        <stop offset="1" stopColor="#9c4622" />
      </radialGradient>
      <linearGradient id={`${id}-ear`} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#f0e0c6" />
        <stop offset="0.45" stopColor="#c05a2f" />
        <stop offset="1" stopColor="#3a2418" />
      </linearGradient>
      <linearGradient id={`${id}-tear`} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#5a2a17" stopOpacity="0.45" />
        <stop offset="1" stopColor="#5a2a17" stopOpacity="0" />
      </linearGradient>
      <linearGradient id={`${id}-body`} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#c86032" />
        <stop offset="0.38" stopColor="#93401f" />
        <stop offset="0.74" stopColor="#3a2418" />
        <stop offset="1" stopColor="#241812" />
      </linearGradient>
      <radialGradient id={`${id}-cheek`} cx="50%" cy="42%" r="60%">
        <stop offset="0" stopColor="#fbf3e6" />
        <stop offset="0.74" stopColor="#f2e3ca" />
        <stop offset="1" stopColor="#e7d3b2" stopOpacity="0" />
      </radialGradient>
      <radialGradient id={`${id}-tail`} cx="42%" cy="34%" r="72%">
        <stop offset="0" stopColor="#cf6a3c" />
        <stop offset="0.68" stopColor="#b0502a" />
        <stop offset="1" stopColor="#7a3418" />
      </radialGradient>
    </defs>
  );
}

const INK = '#20140f';

// Face on a 0 0 64 64 grid. Rust head with flat white splotch markings; eyes carry .nova-eye
// so nova.css can blink them.
function Face({ mood, id }: { mood: NovaMood; id: string }): ReactNode {
  const asleep = mood === 'sleep';
  const blush = mood === 'happy' || mood === 'cheer';
  return (
    <g className="nova-face">
      <path d="M19 20 C 14.5 13 15 6 18.5 4.5 C 22 6 25.5 12 26.5 18 C 24 20 21 21 19 20 Z" fill={`url(#${id}-ear)`} />
      <path d="M45 20 C 49.5 13 49 6 45.5 4.5 C 42 6 38.5 12 37.5 18 C 40 20 43 21 45 20 Z" fill={`url(#${id}-ear)`} />
      <ellipse cx="20" cy="11" rx="2.6" ry="3.4" fill={`url(#${id}-cheek)`} transform="rotate(-20 20 11)" />
      <ellipse cx="44" cy="11" rx="2.6" ry="3.4" fill={`url(#${id}-cheek)`} transform="rotate(20 44 11)" />
      <ellipse cx="32" cy="35" rx="20" ry="18.5" fill={`url(#${id}-head)`} />
      {/* flat white markings (splotches on the head, not pouches) */}
      <ellipse cx="24" cy="37" rx="6.2" ry="5.4" fill={`url(#${id}-cheek)`} />
      <ellipse cx="40" cy="37" rx="6.2" ry="5.4" fill={`url(#${id}-cheek)`} />
      <ellipse cx="32" cy="40" rx="5.6" ry="5.2" fill={`url(#${id}-cheek)`} />
      <ellipse cx="26" cy="27" rx="3.2" ry="3" fill={`url(#${id}-cheek)`} />
      <ellipse cx="38" cy="27" rx="3.2" ry="3" fill={`url(#${id}-cheek)`} />
      {/* rust tear-lines up to the eyes */}
      <path d="M26.4 31 C 25.6 34.5 25.8 39 27 43 L29.4 42.5 C 28.9 38 29 34 29.2 31.4 Z" fill={`url(#${id}-tear)`} />
      <path d="M37.6 31 C 38.4 34.5 38.2 39 37 43 L34.6 42.5 C 35.1 38 35 34 34.8 31.4 Z" fill={`url(#${id}-tear)`} />
      {asleep ? (
        <>
          <path className="nova-eye" d="M23.8 31 q2.6 1.8 5.2 0" stroke={INK} strokeWidth="1.7" strokeLinecap="round" fill="none" />
          <path className="nova-eye" d="M35 31 q2.6 1.8 5.2 0" stroke={INK} strokeWidth="1.7" strokeLinecap="round" fill="none" />
        </>
      ) : (
        <>
          <ellipse className="nova-eye" cx="26.4" cy="31" rx="2.9" ry="3.5" fill={INK} />
          <ellipse className="nova-eye" cx="37.6" cy="31" rx="2.9" ry="3.5" fill={INK} />
          <circle cx="27.5" cy="29.8" r="1" fill="#fff" />
          <circle cx="38.7" cy="29.8" r="1" fill="#fff" />
        </>
      )}
      {blush && (
        <>
          <circle cx="19.5" cy="40" r="2.2" fill="rgba(224,110,64,0.4)" />
          <circle cx="44.5" cy="40" r="2.2" fill="rgba(224,110,64,0.4)" />
        </>
      )}
      <path d="M29.4 38.5 Q32 36.4 34.6 38.5 Q33.6 41.2 32 41.2 Q30.4 41.2 29.4 38.5 Z" fill={INK} />
      <path d="M32 41.2 V43.4" stroke={INK} strokeWidth="1" strokeLinecap="round" />
      <path d="M29.4 43.4 Q32 45.6 34.6 43.4" stroke={INK} strokeWidth="1.1" strokeLinecap="round" fill="none" />
      <path d="M21 39.5 L12 38 M21 41.6 L12.5 42.8 M43 39.5 L52 38 M43 41.6 L51.5 42.8" stroke="rgba(255,255,255,0.5)" strokeWidth="0.7" strokeLinecap="round" />
    </g>
  );
}

export function Nova({
  variant = 'mark',
  mood = 'happy',
  size,
  animated = false,
  ground = false,
  title,
  className,
  style,
}: NovaProps): ReactNode {
  const id = useId().replace(/:/g, '');
  const px = size ?? (variant === 'full' ? 96 : 24);
  const asNum = typeof px === 'number' ? px : undefined;
  const cls = ['nova', `nova--${variant}`, `nova--${mood}`, animated ? 'nova--animated' : '', className].filter(Boolean).join(' ');
  const a11y = title ? { role: 'img' as const, 'aria-label': title } : { 'aria-hidden': true };

  if (variant === 'mark') {
    const dim = asNum !== undefined ? `${asNum}px` : px;
    return (
      <svg className={cls} viewBox="0 0 64 64" width={dim} height={dim} style={style} {...a11y}>
        {title && <title>{title}</title>}
        <Defs id={id} />
        <g className="nova-body">
          <Face mood={mood} id={id} />
        </g>
      </svg>
    );
  }

  const w = asNum !== undefined ? `${asNum}px` : px;
  const h = asNum !== undefined ? `${(asNum * 96) / 80}px` : px;
  return (
    <svg className={cls} viewBox="0 0 80 96" width={w} height={h} style={style} {...a11y}>
      {title && <title>{title}</title>}
      <Defs id={id} />
      {ground && <ellipse className="nova-ground" cx="40" cy="91" rx="26" ry="5.5" fill="var(--nova-ground)" />}
      {/* ringed bushy tail */}
      <g className="nova-tail">
        <path d="M53 78 C 74 78 80 58 72 45 C 68 38 57 39 55 49 C 60 46 67 48 66 56 C 65 65 58 68 51 65 Z" fill={`url(#${id}-tail)`} />
        <path d="M69 48 C 72 52 72 57 69 61" stroke="#2c1d16" strokeWidth="4.2" strokeLinecap="round" fill="none" opacity="0.85" />
        <path d="M62 43 C 66 44 69 48 68 53" stroke="#2c1d16" strokeWidth="3.6" strokeLinecap="round" fill="none" opacity="0.8" />
        <path d="M55 49 C 59 48 64 50 65 55" stroke="#2c1d16" strokeWidth="3.2" strokeLinecap="round" fill="none" opacity="0.7" />
      </g>
      <g className="nova-body">
        {/* lean upright body: rust shoulders blending down into a dark chest/belly */}
        <path d="M40 40 C 26 40 20 54 20 68 C 20 82 28 90 40 90 C 52 90 60 82 60 68 C 60 54 54 40 40 40 Z" fill={`url(#${id}-body)`} />
        <ellipse cx="31" cy="88" rx="6" ry="4.4" fill={INK} />
        <ellipse cx="49" cy="88" rx="6" ry="4.4" fill={INK} />
        <ellipse cx="22" cy="62" rx="5.2" ry="9" fill="#33241d" transform="rotate(-14 22 62)" />
        {mood === 'wave' ? (
          <g className="nova-wave-arm">
            <ellipse cx="60" cy="50" rx="5" ry="8.5" fill="#33241d" transform="rotate(32 60 50)" />
          </g>
        ) : (
          <ellipse cx="58" cy="62" rx="5.2" ry="9" fill="#33241d" transform="rotate(14 58 62)" />
        )}
        <g transform="translate(8 6)">
          <Face mood={mood} id={id} />
        </g>
      </g>
    </svg>
  );
}

/** The compact head logo (brand mark). */
export function NovaMark(props: Omit<NovaProps, 'variant'>): ReactNode {
  return <Nova {...props} variant="mark" />;
}

/**
 * A loading indicator: a small Nova who bobs + blinks while you wait. Accessible status
 * region; honours reduced-motion (she simply sits). Use in place of a bare spinner.
 */
export function NovaSpinner({ label = 'Loading…', size = 44 }: { label?: string; size?: number }): ReactNode {
  return (
    <div className="nova-spinner" role="status" aria-live="polite">
      <Nova variant="full" mood="happy" size={size} animated ground />
      <span className="nova-spinner-label">{label}</span>
    </div>
  );
}
